// src/pages/workflow/WorkflowStart.js
import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

/**
 * Component for starting a new workflow session
 * @param {Object} props
 * @param {string} props.sessionId - Session ID
 * @param {Function} props.onStart - Callback when workflow starts
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 */
function WorkflowStart({ onStart, loading, error }) {
  const [input, setInput] = useState('');

  const exampleIntents = [
    'find VP of sales currently working for Walmart in Bay Area for Camera category',
    'find marketing directors at tech startups in San Francisco with 5+ years experience',
    'find product managers at Fortune 500 companies in New York with SaaS experience'
  ];

  const exampleColors = [
    '#e1ecff', // soft blue
    '#efebff', // soft lavender
    '#fce8f6'  // soft pink
  ];

  const exampleBorderColors = [
    '#b2c7ff', // darker blue
    '#c0b7ff', // darker lavender
    '#f7b6dc'  // darker pink
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) {
      return;
    }

    try {
      await onStart(input);
    } catch (err) {
      console.error('Failed to start workflow:', err);
    }
  };

  const handleExampleClick = (example) => {
    setInput(example);
  };

  return (
    <Box sx={{ maxWidth: 'auto', mx: 'auto', p: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
        Lets go!
        </Typography>
        <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
          Enter your intent to generate an Ideal Customer Profile (ICP). Review and confirm to proceed.
        </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ boxShadow: 3, mb: 3 }}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Example: find VP of sales currently working for Walmart in Bay Area for Camera category"
              variant="outlined"
              disabled={loading}
              sx={{ mb: 2 }}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={loading || !input.trim()}
              startIcon={
                loading
                  ? <CircularProgress size={20} color="inherit" />
                  : <AutoAwesomeIcon />
              }
              sx={{
                bgcolor: '#1BE885',
                color: '#000',
                fontWeight: 600,
                '&:hover': { bgcolor: '#20ec84' },
                '&:disabled': { bgcolor: '#ccc' }
              }}
            >
              {loading ? 'Generating ICP...' : 'Start Now!'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card sx={{ boxShadow: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Example Intents
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {exampleIntents.map((example, index) => (
              <Button
                key={index}
                variant="contained"
                onClick={() => handleExampleClick(example)}
                disabled={loading}
                sx={{
                  bgcolor: exampleColors[index % exampleColors.length],
                  color: '#111827',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  borderColor: exampleBorderColors[index % exampleBorderColors.length],
                  textTransform: 'none',
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  py: 1.5,
                  px: 2,
                  '&:hover': {
                    bgcolor: exampleColors[index % exampleColors.length],
                    filter: 'brightness(0.50)'
                  }
                }}
              >
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  "{example}"
                </Typography>
              </Button>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default WorkflowStart;
